import { useParams, Link } from 'react-router-dom';
import { showcaseProjects } from '../../data';
import { useEffect } from 'react';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import TableOfContents from '../components/TableOfContents';
import BackToTop from '../../sharedComponents/BackToTop';
import ExpandableImage from '../../sharedComponents/ExpandableImage';

const Showcase = () => {
    const { slug } = useParams();
    const project = showcaseProjects.find(p => p.slug === slug);
    
    useDocumentTitle(project ? `${project.title} | Jerel Layog` : 'Showcase | Jerel Layog');

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [slug]);

    const scrollToTop = () => {
        const topHeading = document.getElementById('showcase-top');
        if (topHeading) {
            topHeading.focus({ preventScroll: true });
        }
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    if (!project) {
        return (
            <div className="min-h-screen bg-dark-blue">
                <div className="mx-auto max-w-4xl px-6 py-24 lg:px-12">
                    <Link to="/v1" className="group mb-8 inline-flex items-center font-semibold leading-tight text-cherry-blossom hover:text-off-white">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="mr-1 h-4 w-4 transition-transform group-hover:-translate-x-2">
                            <path fillRule="evenodd" d="M17 10a.75.75 0 01-.75.75H5.612l4.158 3.96a.75.75 0 11-1.04 1.08l-5.5-5.25a.75.75 0 010-1.08l5.5-5.25a.75.75 0 111.04 1.08L5.612 9.25H16.25A.75.75 0 0117 10z" clipRule="evenodd" />
                        </svg>
                        Back to Home
                    </Link>
                    <h1 className="text-4xl font-bold text-off-white">Project not found</h1>
                    <p className="mt-4 text-lg opacity-75">The project you're looking for doesn't exist.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-dark-blue">
            <div className="mx-auto max-w-7xl px-6 py-12 lg:px-12 lg:py-24 relative">
                <Link to="/v1" className="group mb-8 inline-flex items-center font-semibold leading-tight text-cherry-blossom hover:text-off-white transition-colors">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="mr-1 h-4 w-4 transition-transform group-hover:-translate-x-2">
                        <path fillRule="evenodd" d="M17 10a.75.75 0 01-.75.75H5.612l4.158 3.96a.75.75 0 11-1.04 1.08l-5.5-5.25a.75.75 0 010-1.08l5.5-5.25a.75.75 0 111.04 1.08L5.612 9.25H16.25A.75.75 0 0117 10z" clipRule="evenodd" />
                    </svg>
                    Back to Home
                </Link>

                <div className="flex gap-12">
                    <aside className="hidden lg:block w-64 flex-shrink-0">
                        <TableOfContents sections={project.sections} />
                    </aside>

                    <article className="flex-1 max-w-4xl">
                        <header className="mb-12">
                            <h1 id="showcase-top" tabIndex={-1} className="text-4xl font-bold tracking-tight text-off-white sm:text-5xl mb-4">{project.title}</h1>
                            {project.subtitle && <p className="text-xl text-cherry-blossom mb-4">{project.subtitle}</p>}
                            <div className="flex items-center gap-4 text-sm opacity-75"><time dateTime={project.date}>{project.date}</time></div>
                            {project.tags && (
                                <ul className="mt-4 flex flex-wrap gap-2" aria-label="Technologies used">
                                    {project.tags.map((tag, index) => (
                                        <li key={index} className="flex items-center rounded-full bg-cherry-blossom bg-opacity-15 px-3 py-1 text-xs font-medium leading-5 text-cherry-blossom">{tag}</li>
                                    ))}
                                </ul>
                            )}
                        </header>

                        <div className="space-y-12">
                            {project.sections.map((section, index) => {
                                const textSectionIndex = project.sections.slice(0, index).filter(s => s.type === 'text' && (s.heading || s.subheading)).length;
                                const sectionId = section.type === 'text' && (section.heading || section.subheading) ? `section-${textSectionIndex}` : undefined;

                                return (
                                    <section key={index} id={sectionId}>
                                        {section.type === 'image' && (
                                            <figure className="space-y-4">
                                                <ExpandableImage src={section.src} alt={section.alt} variant="v1" />
                                                {section.caption && <figcaption className="text-center text-sm opacity-75 italic">{section.caption}</figcaption>}
                                            </figure>
                                        )}

                                        {section.type === 'text' && (
                                            <div className="prose prose-invert max-w-none">
                                                {section.heading && <h2 className="text-2xl font-bold text-off-white mb-4 scroll-mt-24">{section.heading}</h2>}
                                                {section.subheading && <h3 className="text-xl font-semibold text-off-white mb-3 scroll-mt-24">{section.subheading}</h3>}
                                                <p className="text-base leading-relaxed opacity-90">{section.content}</p>
                                            </div>
                                        )}

                                        {section.type === 'links' && (
                                            <div className="flex flex-wrap gap-4 mt-6">
                                                {section.links.map((link, linkIndex) => (
                                                    <a key={linkIndex} href={link.url} target="_blank" rel="noopener noreferrer" className="group inline-flex items-center gap-2 rounded-full bg-cherry-blossom bg-opacity-15 px-6 py-3 font-semibold text-cherry-blossom transition hover:bg-opacity-25">
                                                        {link.text}
                                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4 transition-transform group-hover:-translate-y-1 group-hover:translate-x-1">
                                                            <path fillRule="evenodd" d="M5.22 14.78a.75.75 0 001.06 0l7.22-7.22v5.69a.75.75 0 001.5 0v-7.5a.75.75 0 00-.75-.75h-7.5a.75.75 0 000 1.5h5.69l-7.22 7.22a.75.75 0 000 1.06z" clipRule="evenodd" />
                                                        </svg>
                                                    </a>
                                                ))}
                                            </div>
                                        )}
                                    </section>
                                );
                            })}
                        </div>
                    </article>
                </div>

                <BackToTop variant="v1" onClick={scrollToTop} />
            </div>
        </div>
    );
}

export default Showcase;